import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { ScreenBackdrop } from './wallet/ScreenBackdrop';

/**
 * Restore a wallet seed on this device (web + TMA after reset / no wallet).
 * If the account already has addresses, the phrase must match them.
 */
export function SeedImportScreen({
  onImport,
  onBack,
  busy,
  error,
}: {
  onImport: (mnemonic: string) => void | Promise<void>;
  /** When the account has no wallet yet, return to the create/import choice. */
  onBack?: () => void;
  busy?: boolean;
  error?: string | null;
}) {
  const { t } = useTranslation();
  const [raw, setRaw] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = () => {
    const mnemonic = raw.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!validateMnemonic(mnemonic, wordlist)) {
      setLocalError(t('login.seedInvalid'));
      return;
    }
    setLocalError(null);
    void onImport(mnemonic);
  };

  const err =
    localError ||
    (error === 'failed'
      ? t('login.seedFailed')
      : error === 'seed_mismatch'
        ? t('login.seedMismatch')
        : error === 'network'
          ? t('login.seedNetwork')
          : error === 'pin_required'
            ? t('login.seedPinRequired')
            : error === 'reauth_required'
              ? t('login.seedReauth')
              : error);

  return (
    <div className="relative flex min-h-dvh flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex w-full flex-1 flex-col px-6 pb-[max(2.5rem,var(--tg-safe-bottom))] pt-[max(2.5rem,var(--tg-safe-top))]">
        <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-ink">
          {t('login.seedTitle')}
        </h1>
        <p className="mt-2 text-[0.9375rem] leading-snug text-ink/55">{t('login.seedSubtitle')}</p>

        <textarea
          className="mt-6 min-h-[9rem] w-full resize-none rounded-2xl border border-black/10 bg-white px-4 py-3 text-[0.9375rem] leading-relaxed text-ink outline-none focus:border-ink/30"
          placeholder={t('login.seedPlaceholder')}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          disabled={busy}
        />

        {err ? <p className="mt-3 text-sm text-red-600">{err}</p> : null}

        <button
          type="button"
          disabled={busy || !raw.trim()}
          onClick={submit}
          className="mt-6 rounded-2xl bg-ink px-4 py-3.5 text-[0.9375rem] font-semibold text-white disabled:opacity-40"
        >
          {busy ? t('login.seedImporting') : t('login.seedImport')}
        </button>
        {onBack ? (
          <button
            type="button"
            disabled={busy}
            onClick={onBack}
            className="mt-3 h-10 text-[13px] font-medium text-ink/55"
          >
            {t('common.back')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** No server wallet (first open or after reset): create new or type the old phrase. */
export function WalletSetupScreen({
  onCreate,
  onImport,
  busy,
}: {
  onCreate: () => void;
  onImport: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="relative flex min-h-screen flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex w-full flex-1 flex-col px-6 pb-[max(2.5rem,var(--tg-safe-bottom))] pt-[max(2.5rem,var(--tg-safe-top))]">
        <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-ink">
          {t('login.setupTitle')}
        </h1>
        <p className="mt-2 text-[0.9375rem] leading-snug text-ink/55">{t('login.setupSubtitle')}</p>
        <div className="mt-auto flex flex-col gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onCreate}
            className="rounded-2xl bg-ink px-4 py-3.5 text-[0.9375rem] font-semibold text-white disabled:opacity-40"
          >
            {t('login.createWallet')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onImport}
            className="rounded-2xl bg-white px-4 py-3.5 text-[0.9375rem] font-semibold text-ink disabled:opacity-40"
          >
            {t('login.haveSeed')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Shown once after a wallet is created, before the main app opens. */
export function NewSeedScreen({
  mnemonic,
  onContinue,
}: {
  mnemonic: string;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const words = mnemonic.trim().split(/\s+/);
  const copy = () => {
    navigator.clipboard?.writeText(words.join(' ')).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  return (
    <div className="relative flex min-h-screen flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex w-full flex-1 flex-col px-6 pb-[max(2.5rem,var(--tg-safe-bottom))] pt-[max(2.5rem,var(--tg-safe-top))]">
        <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-ink">
          {t('login.newSeedTitle')}
        </h1>
        <p className="mt-2 text-[0.9375rem] leading-snug text-ink/55">{t('login.newSeedBody')}</p>
        <div className="mt-6 grid grid-cols-2 gap-2">
          {words.map((word, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl bg-white px-3 py-2.5">
              <span className="w-5 text-[13px] tabular-nums text-ink/40">{i + 1}</span>
              <span className="text-[15px] font-medium text-ink">{word}</span>
            </div>
          ))}
        </div>
        <button type="button" onClick={copy} className="mt-4 text-[14px] font-medium text-ink/60">
          {copied ? t('profile.seedCopied') : t('profile.seedCopy')}
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="mt-auto rounded-2xl bg-ink px-4 py-3.5 text-[0.9375rem] font-semibold text-white"
        >
          {t('login.newSeedSaved')}
        </button>
      </div>
    </div>
  );
}
